/**
 * @fileoverview Job State Management Hook
 * 
 * Manages the state of optimization jobs - merging updates, categorizing jobs,
 * and handling memory cleanup. Pure state management, no API calls.
 */

import { useState, useCallback } from 'react';
import type { OptimizationJob, OptimizationQueueState, JobStateUpdate } from './types';
import { INITIAL_QUEUE_STATE } from './types';
import { sanitizeJobForMemory, RECENT_COMPLETED_WINDOW_MS } from '../../utils/optimizationJobUtils';
import { optimizationDebugLog } from '../../utils/optimizationDebugLogger';

export interface UseJobStateReturn {
  /** Current queue state */
  state: OptimizationQueueState;
  /** Update jobs from API fetch */
  updateJobsFromFetch: (jobs: OptimizationJob[]) => void;
  /** Add a new job to the queue */
  addJob: (job: OptimizationJob) => void;
  /** Update a specific job */
  updateJob: (jobId: string, updates: Partial<OptimizationJob>) => void;
  /** Remove a job from state */
  removeJobFromState: (jobId: string) => void;
  /** Mark a job as completed with result data */
  markJobCompleted: (jobId: string, resultData: Partial<OptimizationJob>) => void;
  /** Mark a job as failed */
  markJobFailed: (jobId: string, error: string) => void;
  /** Set loading state */
  setLoading: (loading: boolean) => void;
  /** Set error state */
  setError: (error: string | null) => void;
  /** Set connection state */
  setConnected: (connected: boolean) => void;
  /** Set polling state */
  setShouldPoll: (shouldPoll: boolean) => void;
  /** Select a job */
  selectJob: (jobId: string | null) => void;
  /** Reset state to initial */
  resetState: () => void;
}

/**
 * Merge incoming jobs with existing jobs, preserving completed job data
 */
function mergeJobs(
  incoming: OptimizationJob[],
  existing: OptimizationJob[]
): OptimizationJob[] {
  const now = Date.now();

  const mergedJobs = incoming.map((job) => {
    const previous = existing.find((prevJob) => prevJob.id === job.id);
    if (!previous) {
      return job;
    }

    // Prevent status downgrades (completed -> running)
    const wasTerminal = previous.status === 'completed' || previous.status === 'failed';
    const isDowngrade = wasTerminal && previous.status !== job.status;

    if (isDowngrade) {
      return sanitizeJobForMemory({
        ...job,
        status: previous.status,
        progress: previous.status === 'completed' ? 100 : job.progress,
        completedAt: previous.completedAt ?? job.completedAt,
        bestResult: previous.bestResult ?? job.bestResult,
        error: previous.error ?? job.error,
      });
    }

    // Preserve completed job data
    if (previous.status === 'completed') {
      return sanitizeJobForMemory({
        ...job,
        completedAt: job.completedAt ?? previous.completedAt,
        bestResult: job.bestResult ?? previous.bestResult,
        progress: job.progress ?? previous.progress ?? 100,
      });
    }

    // Preserve failed job error
    if (previous.status === 'failed') {
      return sanitizeJobForMemory({
        ...job,
        error: previous.error ?? job.error,
      });
    }

    return job;
  });

  // Preserve recent completed/failed jobs not in incoming list
  const mergedIds = new Set(mergedJobs.map((job) => job.id));
  const preservedJobs = existing
    .filter((prevJob) => {
      if (mergedIds.has(prevJob.id)) return false;
      if (prevJob.status !== 'completed' && prevJob.status !== 'failed') return false;
      
      const referenceTime = prevJob.completedAt
        ? new Date(prevJob.completedAt)
        : new Date(prevJob.startTime);
      return now - referenceTime.getTime() <= RECENT_COMPLETED_WINDOW_MS;
    })
    .map(sanitizeJobForMemory);

  return [...mergedJobs, ...preservedJobs].map(sanitizeJobForMemory);
}

/**
 * Categorize jobs by status
 */
function categorizeJobs(jobs: OptimizationJob[]): {
  activeJobs: OptimizationJob[];
  queuedJobs: OptimizationJob[];
  completedJobs: OptimizationJob[];
  failedJobs: OptimizationJob[];
} {
  return {
    activeJobs: jobs.filter((job) => job.status === 'running'),
    queuedJobs: jobs.filter((job) => job.status === 'queued'),
    completedJobs: jobs.filter((job) => job.status === 'completed'),
    failedJobs: jobs.filter((job) => job.status === 'failed'),
  };
}

/**
 * Select the best job ID to display
 */
function selectBestJobId(
  currentSelectedId: string | null,
  completedJobs: OptimizationJob[]
): string | null {
  const hasSelectedJob = currentSelectedId
    ? completedJobs.some((job) => job.id === currentSelectedId)
    : false;

  if (hasSelectedJob) return currentSelectedId;

  // Select the most recently completed job
  const newestCompleted = completedJobs
    .slice()
    .sort((a, b) => {
      const timeA = a.completedAt ? new Date(a.completedAt).getTime() : new Date(a.startTime).getTime();
      const timeB = b.completedAt ? new Date(b.completedAt).getTime() : new Date(b.startTime).getTime();
      return timeB - timeA;
    })[0];

  return newestCompleted?.id ?? null;
}

/**
 * Hook for managing optimization job state
 */
export function useJobState(): UseJobStateReturn {
  const [state, setState] = useState<OptimizationQueueState>(INITIAL_QUEUE_STATE);

  const updateJobsFromFetch = useCallback((incomingJobs: OptimizationJob[]) => {
    setState((prev) => {
      const combinedJobs = mergeJobs(incomingJobs, prev.jobs);
      const categories = categorizeJobs(combinedJobs);
      const selectedJobId = selectBestJobId(prev.selectedJobId, categories.completedJobs);

      return {
        ...prev,
        jobs: combinedJobs,
        ...categories,
        selectedJobId,
      };
    });
  }, []);

  const addJob = useCallback((job: OptimizationJob) => {
    setState((prev) => {
      const jobs = [...prev.jobs, job];
      const categories = categorizeJobs(jobs);

      return {
        ...prev,
        jobs,
        ...categories,
        shouldPoll: true, // Enable polling when we add a job
      };
    });
  }, []);

  const updateJob = useCallback((jobId: string, updates: Partial<OptimizationJob>) => {
    setState((prev) => {
      const jobIndex = prev.jobs.findIndex((job) => job.id === jobId);
      if (jobIndex === -1) return prev;

      const jobs = [...prev.jobs];
      jobs[jobIndex] = { ...jobs[jobIndex], ...updates };
      const categories = categorizeJobs(jobs);

      return {
        ...prev,
        jobs,
        ...categories,
      };
    });
  }, []);

  const removeJobFromState = useCallback((jobId: string) => {
    setState((prev) => {
      const jobs = prev.jobs.filter((job) => job.id !== jobId);
      const categories = categorizeJobs(jobs);
      const hasActiveWork = categories.activeJobs.length > 0 || categories.queuedJobs.length > 0;

      const selectedJobId = prev.selectedJobId === jobId
        ? (categories.completedJobs[0]?.id ?? null)
        : prev.selectedJobId;

      return {
        ...prev,
        jobs,
        ...categories,
        shouldPoll: prev.shouldPoll && hasActiveWork,
        selectedJobId,
      };
    });
  }, []);

  const markJobCompleted = useCallback((jobId: string, resultData: Partial<OptimizationJob>) => {
    setState((prev) => {
      const jobIndex = prev.jobs.findIndex((job) => job.id === jobId);
      
      let jobs = [...prev.jobs];
      
      if (jobIndex >= 0) {
        jobs[jobIndex] = {
          ...jobs[jobIndex],
          ...resultData,
          status: 'completed',
          progress: 100,
          completedAt: new Date().toISOString(),
        };
      } else {
        // Job not in state - create from completion data
        const newJob: OptimizationJob = {
          id: jobId,
          userId: '',
          status: 'completed',
          jobType: 'optimization',
          priority: 0,
          progress: 100,
          completedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          ...resultData,
        };
        jobs = [...jobs, newJob];
      }

      const categories = categorizeJobs(jobs);
      const hasActiveWork = categories.activeJobs.length > 0 || categories.queuedJobs.length > 0;

      return {
        ...prev,
        jobs,
        ...categories,
        shouldPoll: prev.shouldPoll && hasActiveWork,
        selectedJobId: jobId,
      };
    });
  }, []);

  const markJobFailed = useCallback((jobId: string, error: string) => {
    setState((prev) => {
      const jobIndex = prev.jobs.findIndex((job) => job.id === jobId);
      if (jobIndex === -1) return prev;

      const jobs = [...prev.jobs];
      jobs[jobIndex] = {
        ...jobs[jobIndex],
        status: 'failed',
        error,
      };
      const categories = categorizeJobs(jobs);
      const hasActiveWork = categories.activeJobs.length > 0 || categories.queuedJobs.length > 0;

      return {
        ...prev,
        jobs,
        ...categories,
        shouldPoll: prev.shouldPoll && hasActiveWork,
      };
    });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setConnected = useCallback((connected: boolean) => {
    setState((prev) => ({ ...prev, connected }));
  }, []);

  const setShouldPoll = useCallback((shouldPoll: boolean) => {
    setState((prev) => ({ ...prev, shouldPoll }));
  }, []);

  const selectJob = useCallback((jobId: string | null) => {
    setState((prev) => {
      if (jobId === prev.selectedJobId) return prev;

      if (!jobId) {
        optimizationDebugLog('useJobState', 'Clearing selected job', undefined, { console: false });
        return { ...prev, selectedJobId: null };
      }

      const jobExists = prev.completedJobs.some((job) => job.id === jobId);
      if (!jobExists) {
        optimizationDebugLog('useJobState', 'Attempted to select non-completed job', { jobId }, { console: false });
        return prev;
      }

      optimizationDebugLog('useJobState', 'Selecting completed job', { jobId }, { console: false });
      return { ...prev, selectedJobId: jobId };
    });
  }, []);

  const resetState = useCallback(() => {
    setState(INITIAL_QUEUE_STATE);
  }, []);

  return {
    state,
    updateJobsFromFetch,
    addJob,
    updateJob,
    removeJobFromState,
    markJobCompleted,
    markJobFailed,
    setLoading,
    setError,
    setConnected,
    setShouldPoll,
    selectJob,
    resetState,
  };
}
